import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import API_BASE from '../../../utils/api.js';
import { authFetch } from '../../utils/authFetch';
import notify from '../../../utils/notify';

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PurchaseOrderAttachments({ form, setForm, readOnly = false }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const attachments = Array.isArray(form.attachments) ? form.attachments : [];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      setUploading(true);
      const signRes = await authFetch(
        `${API_BASE}/api/upload/presigned-url?filename=${encodeURIComponent(
          file.name
        )}&type=${encodeURIComponent(file.type || 'application/octet-stream')}&size=${encodeURIComponent(
          file.size
        )}`
      );
      const sign = await signRes.json();
      if (!signRes.ok) throw new Error(sign.error || 'Failed to get upload URL');

      const uploadRes = await fetch(sign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');

      const attachment = {
        id: uuidv4(),
        name: file.name,
        url: sign.fileUrl,
        size: file.size,
        contentType: file.type || 'application/octet-stream',
        visibleToVendor: false,
        visibleToClient: false,
        uploadedAt: new Date().toISOString(),
      };
      setForm((f) => ({
        ...f,
        attachments: [...(Array.isArray(f.attachments) ? f.attachments : []), attachment],
      }));
    } catch (err) {
      console.error('Attachment upload error', err);
      notify(err.message || 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id) => {
    setForm((f) => ({
      ...f,
      attachments: (Array.isArray(f.attachments) ? f.attachments : []).filter((a) => a.id !== id),
    }));
  };

  const toggleVisibility = (id, field) => {
    setForm((f) => ({
      ...f,
      attachments: (Array.isArray(f.attachments) ? f.attachments : []).map((a) =>
        a.id === id ? { ...a, [field]: !a[field] } : a
      ),
    }));
  };

  return (
    <div className="memo-field">
      <label>Attachments</label>

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {attachments.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                flexWrap: 'wrap',
              }}
            >
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  flex: '1 1 140px',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {a.name}
              </a>
              <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                {formatFileSize(a.size)}
              </span>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: '#6b7280',
                  whiteSpace: 'nowrap',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!a.visibleToVendor}
                  disabled={readOnly}
                  onChange={() => toggleVisibility(a.id, 'visibleToVendor')}
                />
                Visible to vendor
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: '#6b7280',
                  whiteSpace: 'nowrap',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!a.visibleToClient}
                  disabled={readOnly}
                  onChange={() => toggleVisibility(a.id, 'visibleToClient')}
                />
                Visible to client
              </label>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  aria-label={`Remove ${a.name}`}
                  style={{
                    border: 'none',
                    background: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
          <button
            type="button"
            className="if-btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : '+ Add attachment'}
          </button>
        </div>
      )}
    </div>
  );
}
